// Configuration pages built on the generic ResourcePage.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Send, Play, Pause, Square, CheckCircle2 } from 'lucide-react';
import { ResourcePage } from '@/components/ResourcePage';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { Field, ErrorBox, DataTable, PageHeader } from '@/components/common';
import { api, errorText } from '@/lib/api';
import { useRefOptions, rowLabel } from '@/hooks/meta';
import { toast } from '@/store/toast';
import { useCan, useAuth } from '@/store/auth';
import { t } from '@/i18n';
import { enumLabel } from '@/lib/utils';
import { ImportButton, ImportDialog } from '@/components/ImportDialog';

const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);
const entries = (v: unknown): [string, unknown][] => (v && typeof v === 'object' && !Array.isArray(v) ? Object.entries(v) : []);

// ---- Agents ---------------------------------------------------------------------------------
export function AgentsPage() {
  return (
    <ResourcePage path="agents" title={t('Agents')} description={t('Agent accounts, SIP identities and live state.')}
      columns={['agent_code', 'name', 'sip_username', 'department', 'status', 'state',
        { key: 'sip_registered', label: 'SIP', render: (r) => Number(r.sip_registered) ? <Badge variant="success">{t('registered')}</Badge> : <Badge variant="secondary">{t('offline')}</Badge> },
        'calls_handled']}
      headerActions={<ImportButton kind="agents" title={t('Import agents')} right="agents.add" refresh={['agents']} />}
      rowActions={(r, refresh) => (
        <AgentStateButtons agent={r} onDone={refresh} />
      )}
    />
  );
}

export function AgentStateButtons({ agent, onDone }: { agent: any; onDone: () => void }) {
  const allowed = useCan('dashboard.agent_state');
  async function act(action: string) {
    try { await api(`/agents/${agent.id}/state`, { body: { action } }); toast.ok(`${agent.agent_code}: ${enumLabel(action)}`); onDone(); }
    catch (e) { toast.error(`${agent.agent_code}: ${errorText(e)}`); }
  }
  if (agent.state === 'LOGGED_OUT' || !allowed) return null;
  return (<>
    {agent.state === 'PAUSED' && <Button size="sm" variant="outline" onClick={() => act('ready')}>{t('Ready')}</Button>}
    {agent.state === 'READY' && <Button size="sm" variant="outline" onClick={() => act('pause')}>{t('Pause')}</Button>}
    <Button size="sm" variant="outline" onClick={() => act('logout')}>{t('Log out')}</Button>
  </>);
}

// ---- Campaigns --------------------------------------------------------------------------------
export function CampaignsPage() {
  const [sel, setSel] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="campaigns" title={t('Campaigns')} description={t('Outbound campaigns, dial method, pacing and retry rules. Agents come from the queues you pick.')}
      columns={['name', 'dial_method', 'status', { key: 'queue_ids', label: t('Queues'), render: (r) => <RefNames path="queues" ids={r.queue_ids} /> },
        'max_cps', 'max_concurrent', 'dial_ratio', 'max_attempts', 'timezone']}
      rowActions={(r, refresh) => (<>
        <CampaignStatusButtons campaign={r} onDone={refresh} />
        <Button size="sm" variant="ghost" onClick={() => setSel(r)}>{t('Agents')}</Button>
      </>)} />
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      {sel && <DialogContent title={t('Agents in {{name}}', { name: sel.name })} description={t("The active members of the campaign's queues. Change them on the Queues page (Members).")} className="max-w-xl"><CampaignAgents campaignId={sel.id} /></DialogContent>}
    </Dialog>
  </>);
}

export function CampaignStatusButtons({ campaign, onDone }: { campaign: any; onDone: () => void }) {
  const allowed = useCan('campaigns.control');
  async function set(status: string) {
    try { await api(`/campaigns/${campaign.id}/status`, { body: { status } }); toast.ok(`${campaign.name}: ${enumLabel(status)}`); onDone(); }
    catch (e) { toast.error(errorText(e)); }
  }
  if (!allowed) return null;
  return (<>
    {campaign.status !== 'ACTIVE' && <Button size="icon" variant="ghost" aria-label={t('Start')} title={t('Start')} onClick={() => set('ACTIVE')}><Play className="text-success" /></Button>}
    {campaign.status === 'ACTIVE' && <Button size="icon" variant="ghost" aria-label={t('Pause')} title={t('Pause')} onClick={() => set('PAUSED')}><Pause /></Button>}
    {campaign.status !== 'STOPPED' && <Button size="icon" variant="ghost" aria-label={t('Stop')} title={t('Stop')} onClick={() => set('STOPPED')}><Square /></Button>}
  </>);
}

// read-only: agents are assigned to queues, a campaign gets the members of its queues
function CampaignAgents({ campaignId }: { campaignId: number }) {
  const members = useQuery({ queryKey: ['campaign-agents', campaignId], queryFn: () => api<{ rows: any[] }>(`/campaigns/${campaignId}/agents`) });
  return (
    <DataTable columns={[{ key: 'agent_code', label: t('Agent') }, { key: 'name', label: t('Name') }, { key: 'queues', label: t('Through queue') },
      { key: 'state', label: t('State'), render: (r) => <StatusBadge value={r.state} /> }]}
      rows={members.data?.rows} empty={t('No agents: pick queues with members in the campaign form.')} />
  );
}

// names of referenced rows (an 'ids' field)
function RefNames({ path, ids }: { path: string; ids: unknown }) {
  const o = useRefOptions(path);
  const list = Array.isArray(ids) ? ids : [];
  if (!list.length) return <span className="text-muted-foreground">-</span>;
  return <>{list.map((id) => o.data?.find((x) => x.id === Number(id))?.name ?? `#${id}`).join(', ')}</>;
}

// ---- Contact lists and contacts ------------------------------------------------------------------
export function ContactListsPage() {
  const nav = useNavigate();
  const canImport = useCan('contacts.import');
  const [importFor, setImportFor] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="contact-lists" title={t('Contact Lists')} description={t('Lists of numbers to dial, each tied to a campaign.')}
      columns={['id', 'name', { key: 'campaign_id', label: t('Campaign'), render: (r) => <CampaignName id={r.campaign_id} /> }, 'status', 'created_at']}
      rowActions={(r) => (<>
        <Button size="sm" variant="ghost" onClick={() => nav(`/admin/contacts?list_id=${r.id}`)}>{t('Contacts')}</Button>
        {canImport && <Button size="sm" variant="ghost" onClick={() => setImportFor(r)}><Upload /> {t('Import')}</Button>}
      </>)} />
    <Dialog open={!!importFor} onOpenChange={(o) => !o && setImportFor(null)}>
      {importFor && <DialogContent title={t('Import contacts into {{name}}', { name: importFor.name })} className="max-w-3xl" description={t('A CSV file with a header row. Match its columns to the contact fields; the phone number is required, and other columns can be kept as custom fields.')}>
        <ImportDialog kind="contacts" query={{ list_id: importFor.id }} onDone={() => setImportFor(null)} />
      </DialogContent>}
    </Dialog>
  </>);
}

function CampaignName({ id }: { id: unknown }) {
  const c = useRefOptions('campaigns');
  if (!id) return <span className="text-muted-foreground">-</span>;
  return <>{c.data?.find((x) => x.id === Number(id))?.name ?? `#${id}`}</>;
}

export function ContactsPage() {
  const [params] = useSearchParams();
  const lists = useRefOptions('contact-lists');
  const [listId, setListId] = React.useState(params.get('list_id') ?? '');
  return (
    <div>
      <PageHeader title={t('Contacts')} description={t('Numbers in the contact lists. Status, attempts and results are set by the dialer.')}>
        <Select className="w-64" value={listId} onChange={(e) => setListId(e.target.value)} aria-label={t('List')}>
          <option value="">{t('All lists')}</option>
          {(lists.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </Select>
      </PageHeader>
      <ResourcePage key={listId} embedded path="contacts" title={listId ? t('Contacts in list') : t('All contacts')}
        fixed={listId ? { list_id: Number(listId) } : undefined}
        columns={['id', 'phone_number', 'first_name', 'last_name', 'status', 'priority', 'attempt_count', 'last_result', 'last_disposition', 'next_attempt_at']} />
    </div>
  );
}

// ---- Dispositions, carriers ----------------------------------------------------------------------
export function DispositionsPage() {
  return <ResourcePage path="dispositions" title={t('Dispositions')} description={t('Call outcomes agents pick after a call. Campaign-specific codes override the global ones.')}
    columns={['code', 'name', 'category', { key: 'campaign_id', label: t('Campaign'), render: (r) => r.campaign_id ? <CampaignName id={r.campaign_id} /> : <Badge variant="outline">{t('all')}</Badge> },
      'is_final', 'adds_dnc', 'creates_callback', 'status']} />;
}

export function CarriersPage() {
  return <ResourcePage path="carriers" title={t('Carriers')} description={t('Carriers group SIP trunks and carry their own CPS and concurrency limits.')}
    columns={['id', 'name', 'status', 'max_cps', 'max_concurrent']} />;
}

export function TrunksPage() {
  const canApply = useCan('routing.edit');
  async function apply(r: any) {
    try { const x = await api(`/trunks/${r.id}/apply`, { method: 'POST' }); toast.ok(t('Gateway {{file}} written and loaded', { file: x.file })); }
    catch (e) { toast.error(errorText(e)); }
  }
  return <ResourcePage path="trunks" title={t('SIP Trunks')} description={t('Gateways to carriers. Apply writes the gateway to the VoIP switch and reloads it.')}
    columns={['name', 'gateway_name', 'proxy', 'port', 'transport', 'register', 'caller_id_number', 'max_cps', 'max_concurrent', 'status']}
    rowActions={(r) => canApply ? <Button size="sm" variant="ghost" onClick={() => apply(r)}><Send /> {t('Apply')}</Button> : null} />;
}

// ---- Inbound trunks (docs/INBOUND_TRUNKS.md) --------------------------------------------------------
export function InboundTrunksPage() {
  const domain = useAuth((s) => s.tenant?.sip_domain) ?? '';
  const who = (r: any) => (r.auth_type === 'REGISTER' ? `${r.username ?? ''}@${r.sip_domain ?? ''}` : r.ips);
  return (
    <ResourcePage path="inbound-trunks" title={t('Inbound trunks')}
      description={t('Carriers and gateways that send calls in: accepted from listed IP addresses on port 5080, or after the trunk registers with a user and password. A number prefix, when set, must start every number and is removed before the number is looked up.')}
      headerActions={<span className="text-xs text-muted-foreground" data-testid="itrunk-server">{t('Registration server')}: {domain}:5060</span>}
      columns={['name', { key: 'auth_type', label: t('Auth type'), render: (r) => enumLabel(r.auth_type) }, { key: 'ips', label: t('IP addresses or user'), render: (r) => <span className="font-mono text-xs">{who(r)}</span> },
                'tech_prefix', 'max_concurrent',
                { key: 'sip_registered', label: t('Registered'), render: (r) => (r.auth_type === 'REGISTER'
                  ? <Badge variant={r.sip_registered ? 'success' : 'secondary'}>{r.sip_registered ? t('Yes') : t('No')}</Badge> : <span className="text-muted-foreground">—</span>) },
                'status']} />
  );
}

// ---- Routing --------------------------------------------------------------------------------------
export function RoutingPage() {
  return (
    <div>
      <PageHeader title={t('Routing')} description={t('Prefix routes to trunks, failover rules, and a tester that shows the trunk order for a number.')} />
      <Tabs defaultValue="routes">
        <TabsList><TabsTrigger value="routes">{t('Routes')}</TabsTrigger><TabsTrigger value="failover">{t('Failover rules')}</TabsTrigger><TabsTrigger value="test">{t('Test a number')}</TabsTrigger></TabsList>
        <TabsContent value="routes" className="mt-4">
          <ResourcePage embedded path="routes" title={t('Carrier routes')} columns={['name', 'prefix', { key: 'trunk_id', label: t('Trunk'), render: (r) => <TrunkName id={r.trunk_id} /> }, 'priority', 'weight', 'cost', 'status']} />
        </TabsContent>
        <TabsContent value="failover" className="mt-4">
          <ResourcePage embedded path="failover-rules" title={t('Failover rules')} columns={['match_type', 'match_value', 'action', 'description']} />
        </TabsContent>
        <TabsContent value="test" className="mt-4"><RouteTester /></TabsContent>
      </Tabs>
    </div>
  );
}

function TrunkName({ id }: { id: unknown }) {
  const tr = useRefOptions('trunks');
  return <>{tr.data?.find((x) => x.id === Number(id))?.name ?? `#${id}`}</>;
}

function RouteTester() {
  const [number, setNumber] = React.useState('');
  const [strategy, setStrategy] = React.useState('');
  const [res, setRes] = React.useState<any>(null);
  const [error, setError] = React.useState<unknown>(null);
  const trunks = useRefOptions('trunks');
  async function go(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try { setRes(await api('/routing/plan', { query: { number, strategy } })); } catch (err) { setError(err); setRes(null); }
  }
  return (
    <Card className="max-w-xl">
      <CardContent className="grid gap-3 pt-4">
        <form onSubmit={go} className="flex flex-wrap items-end gap-2">
          <Field help="routetest.number" label={t('Number')}><Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="15551234567" required /></Field>
          <Field help="routetest.strategy" label={t('Strategy')}><Select value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <option value="">{t('Default')}</option>{['PREFIX', 'PRIORITY', 'LEAST_COST', 'PERCENTAGE', 'CAPACITY', 'CPS'].map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}
          </Select></Field>
          <Button type="submit">{t('Show plan')}</Button>
        </form>
        <ErrorBox error={error} />
        {res && (
          <div className="text-sm">
            <div className="mb-2">{t('Strategy')} <b>{enumLabel(res.strategy)}</b>; {t('trunks tried in this order:')}</div>
            <ol className="list-decimal pl-6">{list(res.trunks).map((id) => <li key={id}>{trunks.data?.find((x) => x.id === Number(id))?.name ?? t('trunk {{id}}', { id })}</li>)}</ol>
            {list(res.trunks).length === 0 && <div className="text-destructive">{t('No trunk can take this number.')}</div>}
            {entries(res.skipped).length > 0 && <div className="mt-2 text-muted-foreground">{t('Skipped:')} {entries(res.skipped).map(([k, v]) => `${k} (${v})`).join(', ')}</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- Inbound: DIDs, schedules, holidays --------------------------------------------------------------
export function InboundPage() {
  const [sched, setSched] = React.useState<any | null>(null);
  async function now(r: any) {
    try { const x = await api(`/inbound-routes/${r.id}/now`); toast.ok(`${r.did}: ${enumLabel(x.state)} → ${enumLabel(x.destination)} ${x.value ?? ''}`); }
    catch (e) { toast.error(errorText(e)); }
  }
  async function schedState(r: any) {
    try { const x = await api(`/schedules/${r.id}/state`); toast.ok(`${r.name}: ${enumLabel(x.state)}`); } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <PageHeader title={t('Inbound')} description={t('DIDs and where their calls go, by opening hours and holidays.')} />
      <Tabs defaultValue="dids">
        <TabsList><TabsTrigger value="dids">{t('DIDs')}</TabsTrigger><TabsTrigger value="schedules">{t('Schedules')}</TabsTrigger><TabsTrigger value="holidays">{t('Holidays')}</TabsTrigger></TabsList>
        <TabsContent value="dids" className="mt-4">
          <ResourcePage embedded path="inbound-routes" title={t('Inbound routes')}
            columns={['did', 'name', 'destination_type', 'destination_value', 'after_hours_type', 'after_hours_value', 'record_calls', 'status']}
            rowActions={(r) => <Button size="sm" variant="ghost" onClick={() => now(r)}>{t('Where now?')}</Button>} />
        </TabsContent>
        <TabsContent value="schedules" className="mt-4">
          <ResourcePage embedded path="schedules" title={t('Schedules')} columns={['id', 'name', 'timezone']}
            rowActions={(r) => (<>
              <Button size="sm" variant="ghost" onClick={() => schedState(r)}>{t('Open now?')}</Button>
              <Button size="sm" variant="ghost" onClick={() => setSched(r)}>{t('Hours')}</Button>
            </>)} />
        </TabsContent>
        <TabsContent value="holidays" className="mt-4">
          <ResourcePage embedded path="holidays" title={t('Holidays')} columns={['name', 'holiday_date', 'recurring', 'open_time', 'close_time', 'schedule_id']} />
        </TabsContent>
      </Tabs>
      <Dialog open={!!sched} onOpenChange={(o) => !o && setSched(null)}>
        {sched && <DialogContent title={t('Opening hours: {{name}}', { name: sched.name })} description={t("Day 1 is Sunday, 7 is Saturday. Times are in the schedule's time zone.")} className="max-w-2xl">
          <ResourcePage embedded path="schedule-hours" title={t('Hours')} fixed={{ schedule_id: sched.id }} columns={['day_of_week', 'open_time', 'close_time']} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

// ---- IVR --------------------------------------------------------------------------------------------
export function IvrPage() {
  const [menu, setMenu] = React.useState<any | null>(null);
  const [option, setOption] = React.useState<any | null>(null);
  async function check(r: any) {
    try {
      const x = await api(`/ivr-menus/${r.id}/check`);
      const p = entries(x.problems);
      if (p.length) toast.error(`${r.name}: ${p.map(([k, v]) => `${k}: ${v}`).join('; ')}`); else toast.ok(t('{{name}}: options {{options}}; no problems', { name: r.name, options: list(x.options).join(', ') }));
    } catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div>
      <ResourcePage path="ivr-menus" title={t('IVR')} description={t('Menus, their key options and the actions each key runs.')}
        columns={['id', 'name', 'greeting', 'timeout_ms', 'max_failures', 'exit_destination', 'status']}
        rowActions={(r) => (<>
          <Button size="sm" variant="ghost" onClick={() => check(r)}><CheckCircle2 /> {t('Check')}</Button>
          <Button size="sm" variant="ghost" onClick={() => setMenu(r)}>{t('Options')}</Button>
        </>)} />
      <Dialog open={!!menu} onOpenChange={(o) => { if (!o) { setMenu(null); setOption(null); } }}>
        {menu && <DialogContent title={t('Options for {{name}}', { name: menu.name })} className="max-w-3xl">
          <ResourcePage embedded path="ivr-options" title={t('Keys')} fixed={{ menu_id: menu.id }} columns={['digits', 'action', 'action_data']}
            rowActions={(r) => <Button size="sm" variant="ghost" onClick={() => setOption(r)}>{t('Actions')}</Button>} />
          {option && <div className="mt-4 border-t pt-4">
            <ResourcePage embedded path="ivr-actions" title={t('Actions for key {{digits}}', { digits: option.digits })} fixed={{ option_id: option.id }} columns={['seq', 'action', 'action_data']} />
          </div>}
        </DialogContent>}
      </Dialog>
    </div>
  );
}

// ---- Queues -------------------------------------------------------------------------------------------
export function QueuesPage() {
  const [sel, setSel] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="queues" title={t('Queues')} description={t('ACD queues, their members (the only place agents are assigned), the campaigns they work and callbacks.')}
      columns={['id', 'name',
        // who takes the calls: the member agents, or a target without agents (migration 025)
        { key: 'delivery', label: t('Delivery'), render: (r) => !r.delivery || r.delivery === 'AGENTS' ? enumLabel(r.strategy)
          : <span data-testid="queue-target">{enumLabel(r.delivery)}: {r.delivery_target} · {t('{{n}} at once', { n: r.max_concurrent_calls })}</span> },
        { key: 'campaign_ids', label: t('Campaigns'), render: (r) => <RefNames path="campaigns" ids={r.campaign_ids} /> },
        { key: 'callback_enabled', label: t('Callback'), render: (r) => Number(r.callback_enabled) ? <Badge variant="success">{t('key {{key}}', { key: r.callback_key })}</Badge> : <span className="text-muted-foreground">{t('off')}</span> },
        'max_wait_time', 'no_agents_action', 'overflow_destination', 'status']}
      rowActions={(r) => (!r.delivery || r.delivery === 'AGENTS') && <Button size="sm" variant="ghost" onClick={() => setSel(r)}>{t('Members')}</Button>} />
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      {sel && <DialogContent title={t('Queue {{name}}', { name: sel.name })} className="max-w-2xl"><QueueMembers queueId={sel.id} /></DialogContent>}
    </Dialog>
  </>);
}

function QueueMembers({ queueId }: { queueId: number }) {
  const isAdmin = useCan('queues.edit');
  const st = useQuery({ queryKey: ['queue-status', queueId], queryFn: () => api(`/queues/${queueId}/status`), refetchInterval: 5000 });
  const agents = useRefOptions('agents');
  const [pick, setPick] = React.useState('');
  const [skill, setSkill] = React.useState(5);
  const members: any[] = Array.isArray(st.data?.members) ? st.data.members : [];
  const codes = new Set(members.map((m) => m.agent_code));
  async function add() {
    try { await api(`/queues/${queueId}/members/${pick}`, { method: 'PUT', body: { priority: 100, skill_level: skill } }); setPick(''); st.refetch(); }
    catch (e) { toast.error(errorText(e)); }
  }
  async function remove(code: string) {
    const a = agents.data?.find((x) => x.agent_code === code);
    if (!a) return;
    try { await api(`/queues/${queueId}/members/${a.id}`, { method: 'DELETE' }); st.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  const c = st.data?.counts ?? {};
  return (
    <div className="grid gap-3">
      <div className="flex gap-3 text-sm"><Badge variant="warning">{t('waiting {{n}}', { n: c.waiting ?? 0 })}</Badge><Badge variant="info">{t('offering {{n}}', { n: c.offering ?? 0 })}</Badge><Badge variant="success">{t('bridged {{n}}', { n: c.bridged ?? 0 })}</Badge></div>
      {isAdmin && <div className="flex gap-2">
        <Select value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">{t('Add an agent…')}</option>
          {(agents.data ?? []).filter((a) => !codes.has(a.agent_code)).map((a) => <option key={a.id} value={a.id}>{rowLabel(a)}</option>)}
        </Select>
        <Input type="number" className="w-24" min={1} max={10} value={skill} onChange={(e) => setSkill(Number(e.target.value))} aria-label={t('Skill level')} />
        <Button onClick={add} disabled={!pick}>{t('Add')}</Button>
      </div>}
      <DataTable rowKey={(r) => r.agent_code} rows={members} columns={[
        { key: 'agent_code', label: t('Agent') }, { key: 'state', label: t('State'), render: (r) => <StatusBadge value={r.state} /> },
        { key: 'status', label: t('Member'), render: (r) => <StatusBadge value={r.status} /> }, { key: 'skill_level', label: t('Skill') },
        { key: 'priority', label: t('Priority') }, { key: 'calls_answered', label: t('Answered') }]}
        actions={isAdmin ? (r) => <Button size="sm" variant="ghost" onClick={() => remove(r.agent_code)}>{t('Remove')}</Button> : undefined} />
    </div>
  );
}

// ---- Broadcast ---------------------------------------------------------------------------------------
export function BroadcastPage() {
  const [sel, setSel] = React.useState<any | null>(null);
  return (<>
    <ResourcePage path="broadcasts" title={t('Broadcast')} description={t('Voice broadcasts: play a recording to contact lists or typed-in numbers, with press-1 to an agent queue or a keypad survey.')}
      columns={['id', 'name', 'mode', 'audio_file', { key: 'list_ids', label: t('Contact lists'), render: (r: any) => <ListNames ids={r.list_ids} /> }, 'max_cps', 'max_concurrent', 'calling_start', 'calling_end', 'status']}
      rowActions={(r, refresh) => (<>
        <BroadcastButtons b={r} onDone={refresh} />
        <Button size="sm" variant="ghost" onClick={() => setSel(r)}>{t('Targets')}</Button>
      </>)} />
    <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
      {sel && <DialogContent title={t('Broadcast {{name}}', { name: sel.name })} className="max-w-3xl"><BroadcastTargets b={sel} /></DialogContent>}
    </Dialog>
  </>);
}

function BroadcastButtons({ b, onDone }: { b: any; onDone: () => void }) {
  const allowed = useCan('broadcasts.control');
  async function set(status: string) {
    try { await api(`/broadcasts/${b.id}/status`, { body: { status } }); toast.ok(`${b.name}: ${enumLabel(status)}`); onDone(); } catch (e) { toast.error(errorText(e)); }
  }
  if (!allowed) return null;
  return (<>
    {b.status !== 'ACTIVE' && b.status !== 'COMPLETED' && <Button size="icon" variant="ghost" aria-label={t('Start')} onClick={() => set('ACTIVE')}><Play className="text-success" /></Button>}
    {b.status === 'ACTIVE' && <Button size="icon" variant="ghost" aria-label={t('Pause')} onClick={() => set('PAUSED')}><Pause /></Button>}
    {b.status !== 'STOPPED' && b.status !== 'COMPLETED' && <Button size="icon" variant="ghost" aria-label={t('Stop')} onClick={() => set('STOPPED')}><Square /></Button>}
  </>);
}

function BroadcastTargets({ b }: { b: any }) {
  const st = useQuery({ queryKey: ['broadcast-status', b.id], queryFn: () => api(`/broadcasts/${b.id}/status`), refetchInterval: 5000 });
  const [text, setText] = React.useState('');
  const canEdit = useCan('broadcasts.edit');
  async function addBulk() {
    const numbers = text.split(/[\s,;]+/).filter(Boolean);
    try { const r = await api(`/broadcasts/${b.id}/targets/bulk`, { body: { numbers } }); toast.ok(t('Added {{added}}, duplicates {{duplicates}}, invalid {{invalid}}', { added: r.added, duplicates: r.duplicates, invalid: r.invalid })); setText(''); st.refetch(); }
    catch (e) { toast.error(errorText(e)); }
  }
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap gap-2 text-sm">
        <StatusBadge value={st.data?.status} /> <span>{t('live calls {{n}}', { n: st.data?.live_calls ?? 0 })}</span>
        {entries(st.data?.targets).map(([k, v]) => <Badge key={k} variant="outline">{enumLabel(k)} {String(v)}</Badge>)}
        {entries(st.data?.keys).map(([k, v]) => <Badge key={k} variant="info">{t('key {{key}}: {{n}}', { key: k, n: String(v) })}</Badge>)}
      </div>
      {(st.data?.survey ?? []).length > 0 && (
        <div className="grid gap-1 text-sm" data-testid="survey-results">
          <div className="font-medium">{t('Survey answers')}</div>
          {st.data.survey.map((q: any) => (
            <div key={q.question_id} className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs">{q.field_name}</span>
              {entries(q.answers).length === 0 ? <span className="text-muted-foreground">{t('no answers yet')}</span>
                : entries(q.answers).map(([k, v]) => <Badge key={k} variant="info">{t('key {{key}}: {{n}}', { key: k, n: String(v) })}</Badge>)}
            </div>
          ))}
        </div>
      )}
      <p className="text-sm text-muted-foreground">{(b.list_ids ?? []).length > 0
        ? t('Numbers come from the contact lists of this broadcast as it dials; you can add more by hand.')
        : t('This broadcast has no contact lists. Add contact lists in the broadcast settings, or add numbers here.')}</p>
      <Tabs defaultValue="targets">
        <TabsList>
          <TabsTrigger value="targets">{t('Targets')}</TabsTrigger>
          {b.mode === 'SURVEY' && <TabsTrigger value="questions">{t('Survey questions')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="targets" className="grid gap-3">
          {canEdit && <div className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t('Paste numbers separated by spaces, commas or new lines')} />
            <Button onClick={addBulk} disabled={!text.trim()}>{t('Add numbers')}</Button>
          </div>}
          <ResourcePage embedded path="broadcast-targets" title={t('Targets')} fixed={{ broadcast_id: b.id }} columns={['phone_number', 'contact_id', 'status', 'attempt_count', 'last_result', 'dtmf_result', 'completed_at']} />
        </TabsContent>
        {b.mode === 'SURVEY' && <TabsContent value="questions">
          <ResourcePage embedded path="broadcast-questions" title={t('Survey questions')} fixed={{ broadcast_id: b.id }}
            columns={['seq', 'field_name', 'audio_file', 'valid_keys', 'timeout_ms', 'max_tries']} />
        </TabsContent>}
      </Tabs>
    </div>
  );
}

function ListNames({ ids }: { ids?: number[] }) {
  const lists = useRefOptions('contact-lists');
  if (!ids || ids.length === 0) return <span className="text-muted-foreground">-</span>;
  const names = new Map((lists.data ?? []).map((l) => [Number(l.id), rowLabel(l)]));
  return <span>{ids.map((id) => names.get(Number(id)) ?? `#${id}`).join(', ')}</span>;
}

